import { useCallback, useEffect, useRef, useState } from 'react';
import fnc from '../mymod/commFunction';
import { isParentAccount } from '../mymod/accountRole';
import { billingPost, isBillingUiEnabled } from './billingApi';

export function usableBalance(data) {
  const value = data?.wallet?.availableCredits ?? data?.availableCredits;
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export default function useBillingWallet() {
  const [state, setState] = useState({ data: null, loading: true, error: '', requiresLogin: false });
  const current = useRef({ generation: 0, token: '', running: false, again: false, mounted: false });
  const refresh = useCallback(async () => {
    const token = fnc.getCookie('appSessionToken') || '';
    const isParent = isParentAccount();
    if (!isBillingUiEnabled() || !token || isParent) {
      current.current.generation += 1;
      current.current.token = token;
      current.current.running = false;
      if (current.current.mounted) setState({ data: isBillingUiEnabled() ? null : { enabled: false }, loading: false, error: '', requiresLogin: isBillingUiEnabled() && !token && !isParent });
      return;
    }
    if (current.current.running && current.current.token === token) { current.current.again = true; return; }
    const generation = ++current.current.generation;
    const previousToken = current.current.token;
    current.current.token = token;
    current.current.running = true;
    current.current.again = false;
    setState((previous) => ({ ...previous, data: previousToken === token ? previous.data : null, loading: true, error: '', requiresLogin: false }));
    try {
      const data = await billingPost('wallet');
      if (!current.current.mounted || current.current.generation !== generation || fnc.getCookie('appSessionToken') !== token) return;
      if (data?.enabled !== false && usableBalance(data) === null) throw new Error('잔액을 확인하지 못했습니다. 다시 확인해 주세요.');
      setState({ data, loading: false, error: '', requiresLogin: false });
    } catch (error) {
      if (current.current.mounted && current.current.generation === generation && fnc.getCookie('appSessionToken') === token) {
        setState({ data: null, loading: false, error: error.message || '잔액을 확인하지 못했습니다.', requiresLogin: error.response?.status === 401 });
      }
    } finally {
      if (current.current.generation === generation) {
        current.current.running = false;
        if (current.current.again && current.current.mounted) refresh();
      }
    }
  }, []);

  useEffect(() => {
    const requestState = current.current;
    requestState.mounted = true;
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    const storage = (event) => { if (!event.key || ['appSessionToken', 'appSessionExpiresAt'].includes(event.key)) refresh(); };
    window.addEventListener('billing:updated', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('storage', storage);
    document.addEventListener('visibilitychange', visible);
    refresh();
    return () => {
      requestState.mounted = false;
      requestState.generation += 1;
      requestState.running = false;
      window.removeEventListener('billing:updated', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('storage', storage);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [refresh]);
  return { ...state, refresh, balance: usableBalance(state.data) };
}
